import { cloneDeep } from 'lodash';

import { calcClientDerivatives } from '../calcClientDerivatives';
import { aggregateMetrics, isCoupleScenario } from './aggregation';
import { hydrateClientBirthDates } from './hydration';
import { applyInjectedPolicies, applyInjectedSelfFundingChanges } from './injection';
import type { ComparisonScenario, ComparisonScenarioInput } from './types';

/**
 * Produce a fully-derived, couple-aware comparison scenario from a set of
 * caller inputs.
 *
 * Single source of truth for comparison-level aggregation: consumers
 * funnel through this helper rather than re-deriving clients and re-
 * aggregating partner contributions on their own.
 *
 * Contract:
 *
 * - **Pure / no caller mutation.** Inputs are deep-cloned before any
 *   transformation. Callers may pass live Redux state without worrying
 *   about the helper writing back.
 * - **Partner derived first.** The engine's joint-policy logic reads
 *   `partner.allPhaseCosts.*` and `partner.intakeSurvey.clientPhaseStartAges`
 *   while computing primary's derivatives. Partner clients fetched via
 *   React Query don't have those fields populated, so we always derive a
 *   fresh partner before deriving primary-with-partner-attached.
 * - **Couple mode is implicit.** Detected from `(joint policy in primary,
 *   partner present)`. A joint quote without a partner silently falls
 *   back to single mode.
 * - **Injection is typed.** Caller supplies `injectedPolicies` and
 *   `injectedSelfFundingChanges`; the helper applies them on its internal
 *   clone of primary. No caller-side mutation.
 * - **`includePrimaryExistingPolicies` is primary-only.** Partner's
 *   existing policies are always preserved, since the guided planning
 *   "Existing LTC" toggle is a primary-client signal.
 */
export function calcComparisonScenario(input: ComparisonScenarioInput): ComparisonScenario {
  const {
    primary,
    partner = null,
    injectedPolicies = [],
    injectedSelfFundingChanges = [],
    includePrimaryExistingPolicies = true,
    isCoupleMode: forcedCoupleMode,
  } = input;

  // 1. Prepare primary clone.
  const primaryClone = cloneDeep(primary);
  // `mutableClientPartner` is derived scratch state, not an input to this
  // helper. Only the explicit `partner` argument is authoritative; otherwise a
  // previously-derived partner embedded on `primary` can silently turn a
  // single-person comparison back into a couple calculation.
  primaryClone.mutableClientPartner = null;
  hydrateClientBirthDates(primaryClone);

  if (!includePrimaryExistingPolicies && primaryClone.multipleFundingSources?.ltcPolicy) {
    primaryClone.multipleFundingSources.ltcPolicy = {};
  }

  applyInjectedPolicies(primaryClone, injectedPolicies);
  applyInjectedSelfFundingChanges(primaryClone, injectedSelfFundingChanges);

  // 2. Prepare partner clone (no policy stripping — partner's funding
  //    sources are independent of the primary-only "Existing LTC" toggle).
  const partnerClone = partner ? cloneDeep(partner) : null;

  if (partnerClone) {
    // Like the primary's embedded partner above, this is derived scratch state.
    // The explicit partner must be derived on their own before being attached
    // to primary; otherwise a stale nested client can consume their joint pool.
    partnerClone.mutableClientPartner = null;
    hydrateClientBirthDates(partnerClone);
  }

  // 3. Derive partner first so the joint-policy logic in primary's
  //    derivation can read partner's `allPhaseCosts`,
  //    `intakeSurvey.clientPhaseStartAges`, etc.
  const derivedPartner = partnerClone
    ? (calcClientDerivatives(partnerClone, null) as Client)
    : null;

  // 4. Derive primary with the freshly-derived partner attached.
  const derivedPrimary = calcClientDerivatives(primaryClone, derivedPartner) as Client;
  const comparisonPartner = derivedPartner
    ? (derivedPrimary.mutableClientPartner ?? derivedPartner)
    : null;

  // 5. Decide couple mode. The override (if supplied) wins: callers that
  //    compare two scenarios for the same household need a consistent
  //    couple-mode flag across both, even when one of them has no joint
  //    policy in derived state. When the override is omitted, auto-detect
  //    from primary's derived policies. Partner must be non-null either
  //    way — passing `isCoupleMode: true` with no partner is a no-op.
  const isCoupleMode =
    forcedCoupleMode !== undefined
      ? forcedCoupleMode && comparisonPartner !== null
      : isCoupleScenario(derivedPrimary, comparisonPartner);

  // 6. Aggregate.
  const metrics = aggregateMetrics(derivedPrimary, comparisonPartner, isCoupleMode);

  return {
    isCoupleMode,
    partnerFirstName: isCoupleMode ? (comparisonPartner?.clientFirstName ?? null) : null,
    ...metrics,
    _derivedPrimary: derivedPrimary,
    _derivedPartner: comparisonPartner,
  };
}
